import React, { useEffect, useState } from "react";
import { Button, Card, Col, Stack } from "react-bootstrap";
import IconButton from "../../ui/IconButton";
import { unsavedChanges } from "../../../App";
import { faCheck, faClose, faEdit, faInfoCircle, faTrash } from "@fortawesome/free-solid-svg-icons";
import Input from "../../ui/Input";
import { deleteResource, patchResource } from "../../../services/httpClient";
import SelectObjectTypeahead from "../../ui/SelectObjectTypeahead";

interface OrgResourceCardState {
  resource: any;
  getResources: any;
  resourceCategories: any;
  onAddResourceCategory: any;
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  setErrMsg: any;
}

const OrgResourceCard = ({
  resource,
  getResources,
  resourceCategories,
  onAddResourceCategory,
  setOrgToSave,
  setShowToast,
  setToastText,
  setToastBody,
  setErrMsg,
}: OrgResourceCardState) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [resourceName, setResourceName] = useState<string>("");
  const [resourceCategory, setResourceCategory] = useState<any>();
  const [resourceQuantity, setResourceQuantity] = useState<number>(resource?.quantity);

  const consumable = resource?.type_key === "consumable";
  const nonConsumable = resource?.type_key === "nonconsumable";
  const numAssigned = resource?.quantity - resource?.remaining;

  useEffect(() => {
    setResourceName(resource?.name);
    const category = resourceCategories?.find(
      (cat: { id: number; organization_id: number; name: string; key: string }) => cat.key === resource?.category_key,
    );
    setResourceCategory(category || null);
    setResourceQuantity(resource?.quantity);
  }, [resource]);

  useEffect(() => {
    if (resourceCategory?.id && resourceCategories.length > 0) {
      const updatedCategory = resourceCategories.find((c: any) => c.id === resourceCategory.id);
      if (updatedCategory && updatedCategory !== resourceCategory) {
        setResourceCategory(updatedCategory);
      }
    }
  }, [resourceCategories]);

  const onClickEdit = () => {
    setIsEditing(true);
  };

  const onClickEditFinal = async () => {
    const updatedResource = {
      name: resourceName,
      category: resourceCategory?.key,
      quantity: resourceQuantity,
    };
    await patchResource(updatedResource, resource?.id)
      .then((res: any) => {
        unsavedChanges.value = false;
        setOrgToSave(false);
        setShowToast(true);
        setToastText("Resource Updated");
        setToastBody(resourceName);
        setIsEditing(false);
      })
      .then(() => {
        getResources(true);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelEdit = () => {
    unsavedChanges.value = false;
    setResourceName(resource?.name);
    setResourceQuantity(resource?.quantity);
    setResourceCategory(resource?.category_key || "0");
    setOrgToSave(false);
    setIsEditing(false);
    setErrMsg("");
  };

  const onUpdateName = (e: any) => {
    setResourceName(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMsg("");
  };

  const onUpdateCategory = (e: any) => {
    setResourceCategory(e);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMsg("");
  };

  const onUpdateQuantity = (e: any) => {
    setResourceQuantity(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMsg("");
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const onClickDeleteFinal = () => {
    deleteResource(resource?.id)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Resource Deleted");
        setToastBody(resourceName);
        setIsDeleting(false);
        getResources(true);
      })
      .catch((err: any) => {
        setIsDeleting(false);
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setErrMsg("");
  };

  const renderEdit = () => {
    return (
      <Stack direction="horizontal" gap={3}>
        <Col className="flex-grow-1">
          <Input label="Name" type="text" value={resourceName} onUpdate={(e: any) => onUpdateName(e)} hasFocus={true} />
        </Col>
        <Col className="flex-grow-1">
          <SelectObjectTypeahead
            label="Resource Category"
            value={resourceCategory}
            options={resourceCategories}
            onUpdate={(e: any) => {
              onUpdateCategory(e);
            }}
            allowCreate={true}
            onCreateOption={async (newCategoryName: string) => {
              const newCategory = await onAddResourceCategory(newCategoryName);
              onUpdateCategory(newCategory);
              return newCategory;
            }}
          />
        </Col>
        {nonConsumable && <IconButton tooltipText={`${numAssigned} Currently Assigned`} icon={faInfoCircle} className="ms-1" />}
        <Col xs={2}>
          <Input
            label={consumable ? "Available" : "Quantity"}
            type="number"
            value={resourceQuantity}
            onUpdate={(e: any) => onUpdateQuantity(e)}
          />
        </Col>
        <Stack direction="horizontal" gap={3}>
          <IconButton onClickButton={onClickEditFinal} tooltipText={"Save"} icon={faCheck} />
          <div className="vr" />
          <IconButton onClickButton={onClickCancelEdit} tooltipText={"Cancel"} icon={faClose} />
        </Stack>
      </Stack>
    );
  };

  const renderDelete = () => {
    return (
      <Stack direction="horizontal" gap={3}>
        <Stack style={{ justifyContent: "center" }} gap={2}>
          <Card.Subtitle className="m-0">Delete {resource?.name}?</Card.Subtitle>
        </Stack>
        <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
          Confirm Delete
        </Button>
        <div className="vr" />
        <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
          Cancel
        </Button>
      </Stack>
    );
  };

  return (
    <Card key={resource?.name} className="bg-light mb-2">
      <Card.Body>
        {isEditing ? (
          <>{renderEdit()}</>
        ) : isDeleting ? (
          <>{renderDelete()}</>
        ) : (
          <Stack direction="horizontal" gap={3}>
            <Stack className="align-self-center">
              <Card.Subtitle className="m-0">
                <small>{resource?.name}</small>
              </Card.Subtitle>
            </Stack>
            <small className="text-capitalize">{resource?.category_name}</small>
            {resource?.category_name && <div className="vr" />}
            {consumable ? (
              <small>Available: {resource?.quantity}</small>
            ) : (
              <small>
                Available: {resource?.remaining}/{resource?.quantity}
              </small>
            )}
            <div className="vr" />
            <IconButton onClickButton={onClickEdit} tooltipText={"Edit"} icon={faEdit} />
            <div className="vr" />
            {nonConsumable && numAssigned > 0 ? (
              <IconButton onClickButton={onClickDelete} tooltipText={"Must Unassign Resources"} icon={faTrash} disabled={true} />
            ) : (
              <IconButton onClickButton={onClickDelete} tooltipText={"Delete"} icon={faTrash} />
            )}
          </Stack>
        )}
      </Card.Body>
    </Card>
  );
};

export default OrgResourceCard;
